import React, { useEffect, useState } from "react";
import {
  Clock,
  Loader2,
  Phone,
  AlertTriangle,
  UserCheck,
  Info,
  X,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import {
  CheckLeadByPhoneNo,
  RequestLeadTransfer,
} from "../../Leads/Services/LeadService";
import InitiateCallDrawer from "./InitiateCallDrawer";

const PhoneVerificationDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [checking, setChecking] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [leadInfo, setLeadInfo] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showContinueForm, setShowContinueForm] = useState(false);
  const [showInitiateCall, setShowInitiateCall] = useState(false);

  //   reset the drawer
  //   useEffect(() => {
  //   if (open && mode === "verify") {
  //     setDuplicate(false);
  //     setLeadInfo(null);
  //     setPhone("");
  //     setShowContinueForm(false);
  //   }
  // }, [open, mode]);

  const handleVerify = async () => {
    if (!phone.trim()) {
      setAlert({ type: "error", message: "Please enter phone number" });
      return;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      setAlert({
        type: "error",
        message: "Enter valid 10-digit number",
      });
      return;
    }

    try {
      setChecking(true);

      const res = await CheckLeadByPhoneNo(phone);
      console.log(res);
      // NEW LEAD
      if (res?.status === "success") {
        setAlert({
          type: "success",
          message: res?.message || "Mobile verified",
        });

        setTimeout(() => {
          onClose();

          navigate(`/Add-Lead?phone=${phone}`);
        }, 1500);

        setDuplicate(false);
        setLeadInfo(null);
      }

      // FOUND LEAD
      else if (res?.status === "found") {
        setAlert({
          type: "success",
          message: res?.message || "Lead found",
        });

        setTimeout(() => {
          onClose();

          navigate(`/Add-Lead?phone=${phone}`, {
            state: {
              lead: res?.leadDetails,
            },
          });
        }, 2500);
      }

      // DUPLICATE
      else if (res?.status === "duplicate") {
        setDuplicate(true);
        setLeadInfo(res);
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: err?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setChecking(false);
    }
  };
  const handleRequestTransfer = async () => {
    try {
      const res = await RequestLeadTransfer(leadInfo.leadUID);

      if (res?.status === "success") {
        if (res?.transferType === 0) {
          setAlert({
            type: "error",
            message: res?.message,
          });
          // handleReset();
        } else if (res?.transferType === 1) {
          setAlert({
            type: "success",
            message: res?.message,
          });
           setShowInitiateCall(true);
          // no navigation yet (future edit page)
        } else if (res?.transferType === 2) {
          setAlert({
            type: "success",
            message: res?.message,
          });
          handleReset();
        }
      } else {
        setAlert({
          type: "error",
          message: res?.message || "Failed to request transfer",
        });
      }
    } catch (err) {
      setAlert({
        type: "error",
        message: "Failed to request transfer",
      });
    }
  };

  const handleReset = () => {
    setPhone("");
    setDuplicate(false);
    setLeadInfo(null);
    setChecking(false);
    setShowInitiateCall(false);
    setChecking(false);
  };

  return (
    <>
      {/* 1. Main Wrapper - Handles visibility of the entire portal */}
      <div
        className={`fixed inset-0 z-50 ${
          open && !showInitiateCall ? "visible" : "invisible"
        } transition-all duration-300`}
      >
        {/* 2. Overlay - Now absolute/fixed to cover the FULL screen regardless of the drawer */}
        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${open && !showInitiateCall ? "opacity-100" : "opacity-0"}`}
          onClick={onClose}
        />

        {/* 3. Drawer - Now fixed to the right side independently */}
        <div
          className={`fixed top-0 right-0 w-full max-w-xl bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-500 ease-in-out
                      ${
                        open && !showInitiateCall
                          ? "translate-x-0"
                          : "translate-x-full"
                      }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b">
            <div className="flex items-center gap-2">
              <Phone className="text-blue-600" />
              <h2 className="font-semibold text-lg">Verify Mobile Number</h2>
            </div>
            <X onClick={onClose} className="cursor-pointer text-gray-400" />
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Body */}
            <div className="p-5 flex flex-col gap-5 flex-1">
              {/* INPUT */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Phone Number <span className="text-red-500">*</span>
                </label>

                <div
                  className={`flex items-center border-2 rounded-xl px-4 py-3 transition
                              ${
                                duplicate
                                  ? "border-gray-300 bg-gray-100 opacity-80"
                                  : "border-gray-200"
                              }`}
                >
                  <span className="mr-2 text-gray-400">+91</span>

                  <input
                    value={phone}
                    maxLength={10}
                    onChange={(e) => {
                      if (duplicate) return;
                      setPhone(e.target.value);
                      setDuplicate(false);
                      setLeadInfo(null);
                    }}
                    className={`w-full outline-none text-sm
                                ${
                                  duplicate
                                    ? "bg-transparent text-gray-500 cursor-not-allowed pointer-events-none"
                                    : "text-gray-800"
                                }`}
                    placeholder="Enter mobile number"
                    readOnly={duplicate}
                    tabIndex={duplicate ? -1 : 0}
                  />

                  {phone.length === 10 && !checking && !duplicate && (
                    <UserCheck size={16} className="text-green-500" />
                  )}
                  {duplicate && <Lock size={16} className="text-gray-600" />}
                </div>
              </div>

              {/* Duplicate Lead Warning */}
              {duplicate && leadInfo && (
                <div
                  className={`p-5 rounded-xl border ${
                    leadInfo.canEdit
                      ? "bg-blue-50 border-blue-200"
                      : "bg-yellow-50 border-yellow-200"
                  }`}
                >
                  {/* Header */}
                  <div
                    className={`flex items-center gap-2 font-semibold mb-3 ${
                      leadInfo.canEdit ? "text-blue-700" : "text-yellow-700"
                    }`}
                  >
                    {leadInfo.canEdit ? (
                      <UserCheck size={18} />
                    ) : (
                      <AlertTriangle size={18} />
                    )}

                    {leadInfo.canEdit
                      ? "Continue Your Lead"
                      : "Lead Already Exists"}
                  </div>

                  {/* Info Row (always visible) */}
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        leadInfo.canEdit
                          ? "bg-blue-100 text-blue-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {leadInfo.name?.charAt(0)}
                    </div>

                    <div>
                      <p className="font-semibold text-gray-800">
                        {leadInfo.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        Assigned to{" "}
                        <span className="font-medium">
                          {leadInfo.ownerName}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* EXTRA CONTENT → ONLY when NOT continue editing */}
                  {!showContinueForm && (
                    <div className="bg-white border rounded-lg p-3 text-sm space-y-3 mt-4">
                      {leadInfo.canEdit ? (
                        <p className="text-gray-700">
                          You already own this lead. Continue where you left
                          off.
                        </p>
                      ) : (
                        <p className="text-gray-600">
                          You cannot create a new lead with this number.
                          However, you can request ownership transfer.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* LEAD HANDLING GUIDELINES */}
              {!showContinueForm && (
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-100">
                      <Info size={16} className="text-blue-600" />
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">
                        Lead Handling Guidelines
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Follow these rules while verifying and creating leads to
                        avoid duplication and ownership conflicts.
                      </p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gray-200" />

                  {/* Rules */}
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-3">
                      <UserCheck size={16} className="text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-800">
                          New Mobile Number
                        </p>
                        <p className="text-xs text-gray-500">
                          If the number is not found, you can safely create a
                          new lead.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        size={16}
                        className="text-yellow-600 mt-0.5"
                      />
                      <div>
                        <p className="font-medium text-gray-800">
                          Existing Lead
                        </p>
                        <p className="text-xs text-gray-500">
                          If a lead already exists, you cannot create a
                          duplicate. You may request ownership transfer.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock size={16} className="text-orange-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-800">
                          Inactive Leads
                        </p>
                        <p className="text-xs text-gray-500">
                          Leads inactive for more than 6 months will be
                          transferred without approval.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        size={16}
                        className="text-red-500 mt-0.5"
                      />
                      <div>
                        <p className="font-medium text-gray-800">
                          Recently Active Leads
                        </p>
                        <p className="text-xs text-gray-500">
                          Active leads require approval from the current owner
                          before transfer.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-5 border-t flex gap-3">
            {!duplicate ? (
              <button
                onClick={handleVerify}
                disabled={checking}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white transition
      ${checking ? "bg-blue-400" : "bg-blue-700 hover:bg-blue-800"}`}
              >
                {checking ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Verifying...
                  </>
                ) : (
                  "Verify Phone Number"
                )}
              </button>
            ) : (
              <>
                <button
                  onClick={handleReset}
                  className="w-1/2 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  Try Another Number
                </button>

                {leadInfo?.canEdit ? (
                  <button
                    onClick={() => {
                      setShowInitiateCall(true);
                    }}
                    className="w-1/2 py-3 rounded-xl bg-blue-700 text-white hover:bg-blue-800"
                  >
                    Continue Editing
                  </button>
                ) : (
                  <button
                    onClick={handleRequestTransfer}
                    className="w-1/2 py-3 rounded-xl bg-blue-700 text-white hover:bg-blue-800"
                  >
                    Request Transfer
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/*  Notification Alert */}
      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}

      <InitiateCallDrawer
        open={showInitiateCall}
        onClose={() => setShowInitiateCall(false)}
        lead={{
          ...leadInfo,
          phoneNo: phone,
        }}
      />
    </>
  );
};

export default PhoneVerificationDrawer;
